/**
 * CompanyCard — the "Company" section in the unified Profile card
 * (was WorkspaceCard). Read-only summary of the active company with
 * quick-jump actions into the relevant settings pages.
 *
 * Layout:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  COMPANY                                                     │
 *   │  Your active workspace.                                      │
 *   │                                                              │
 *   │  ┌───┐  Loomwell Textiles            [Founder]               │
 *   │  │ 🏢│  Loomwell Textiles Pvt Ltd                            │
 *   │  └───┘                                                       │
 *   │                                                              │
 *   │  ┌──────────────────────────────────────────────────────┐    │
 *   │  │ REGISTERED ADDRESS                                    │    │
 *   │  │ Plot 12, Peenya Industrial Area Phase II              │    │
 *   │  │ Bengaluru, Karnataka 560058                           │    │
 *   │  │ India                                                 │    │
 *   │  └──────────────────────────────────────────────────────┘    │
 *   │                                                              │
 *   │  Company profile →   GSTIN & branches →   Team →             │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Fetches full company details via `companies.get` for the address
 * fields not carried by `identity.me` (which only has `state`).
 *
 * No outer Card — the route owns the wrapper Card with hairline section
 * dividers.
 */
import { Building2, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { StatusPill } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import type { MeCompany } from "../../lib/session.ts";

export interface WorkspaceCardProps {
  company: NonNullable<MeCompany>;
}

export function WorkspaceCard({ company }: WorkspaceCardProps) {
  const displayName = company.tradeName ?? company.legalName;
  const showLegalLine = Boolean(company.tradeName && company.tradeName !== company.legalName);

  // Fetch full company details for address fields not carried by `identity.me`.
  const companyDetail = trpc.companies.get.useQuery({});

  const address = companyDetail.data?.address ?? null;
  const city = companyDetail.data?.city ?? null;
  const state = companyDetail.data?.state ?? company.state;
  const pincode = companyDetail.data?.pincode ?? null;
  const companyEmail = companyDetail.data?.email ?? null;
  const companyPhone = companyDetail.data?.phone ?? null;

  return (
    <section className="px-5 sm:px-6 py-5 sm:py-6 flex flex-col gap-5">
      <header>
        <h2 className="text-sm font-semibold text-ink uppercase tracking-wider">Company</h2>
        <p className="text-sm text-muted mt-1">Your active workspace.</p>
      </header>

      {/* Identity row — big chip + trade name + role pill. */}
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className="inline-grid place-items-center h-12 w-12 rounded-md bg-sky-50 text-sky-700 shrink-0 ring-1 ring-sky-100"
        >
          <Building2 size={22} strokeWidth={2} />
        </span>

        <div className="min-w-0 flex-1 flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-ink truncate">{displayName}</h3>
            <StatusPill tone="sky" size="sm">
              {company.role}
            </StatusPill>
          </div>
          {showLegalLine ? (
            <div className="text-sm text-muted truncate">{company.legalName}</div>
          ) : null}
        </div>
      </div>

      {/* Company details — address (full-width) + 2×3 grid. */}
      <div className="bg-bg/60 ring-1 ring-line rounded-md px-4 py-3 flex flex-col gap-3">
        <AddressField label="Street address" value={address} />
        <div className="grid grid-cols-3 gap-4">
          <AddressField label="City" value={city} />
          <AddressField label="State" value={state} />
          <AddressField label="PIN code" value={pincode} />
          <AddressField label="Country" value="India" />
          <AddressField label="Registered email" value={companyEmail} />
          <AddressField label="Business phone" value={companyPhone} />
        </div>
      </div>

      {/* Quick links into the relevant settings sub-pages. */}
      <nav className="flex gap-4">
        <QuickLink to="/settings/company">Company profile</QuickLink>
        <QuickLink to="/settings/gstin">GSTIN &amp; branches</QuickLink>
        <QuickLink to="/settings/team">Team &amp; roles</QuickLink>
      </nav>
    </section>
  );
}

function AddressField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-0.5 w-full">
      <span className="text-xs font-medium text-muted">{label}</span>
      <span className={`text-sm ${value ? "text-ink" : "text-muted italic"}`}>
        {value ?? "Not set"}
      </span>
    </div>
  );
}

function QuickLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to as never}
      className="group flex-1 flex items-center gap-1.5 py-2 text-sm text-sky-700 hover:text-sky-900 transition-colors"
    >
      {children}
      <ArrowRight
        size={14}
        strokeWidth={2}
        aria-hidden
        className="group-hover:translate-x-0.5 transition-transform"
      />
    </Link>
  );
}
